import React, {
    useCallback,
    useEffect,
    useMemo,
    useRef,
    useState
} from "react";

import { css } from "@compiled/react";

import { AnimatedModal } from "@/components/adg4/animated-modal/src";

import { SpiderWebContainerGraph } from "./spiderweb-container-graph";
import {
    type ConnectorType,
    type FillColor,
    type ModalContent,
    type SpiderWebContainerProps,
    type WebNodeConfig
} from "./spiderweb-types";
import { getFillColor, VIEWBOX_SCALE } from "./spiderweb-utils";
import { useSpiderWebConnectorPlan } from "./use-spiderweb-connector-plan";
import { useSpiderwebDocumentSectionProgress } from "./use-spiderweb-document-section-progress";
import { useSpiderWebIntersectionVisibility } from "./use-spiderweb-intersection-visibility";
import {
    anyNodeHasPhases,
    useSpiderwebPhasePositions
} from "./use-spiderweb-phase-positions";
import { useSpiderWebReducedMotion } from "./use-spiderweb-reduced-motion";
import { useSpiderwebViewportAspect } from "./use-spiderweb-viewport-aspect";

const NODE_STAGGER = 0.06;
const EDGE_DELAY_BASE = 0.3;
const EDGE_STAGGER = 0.08;
const NODE_ENTRANCE_DURATION_SEC = 0.6;
const CONNECTOR_SOLID_DRAW_DURATION_SEC = 1;
const CONNECTOR_DASHED_ENTRANCE_DURATION_SEC = 0.8;
const OVERLAP_LOAD_ENTRANCE_FADE_SEC = 0.5;
const ANIMATED_CONNECTOR_SHAPE_OPACITY_SEC = 0.6;
const ANIMATED_CONNECTOR_SHAPE_APPEAR_OFFSET_SEC = 1;

/**
 * `animation-play-state` does not inherit; this scopes `paused` to all descendants
 * (and common pseudo-elements) so keyframe animations freeze in place.
 */
/* eslint-disable @atlaskit/ui-styling-standard/no-nested-selectors -- intentional descendant `animation-play-state` (non-inherited) */
const spiderwebPauseScopeStyle = css({
    '&[data-spiderweb-animations-paused="true"] *, &[data-spiderweb-animations-paused="true"] *::before, &[data-spiderweb-animations-paused="true"] *::after':
        {
            animationPlayState: "paused"
        }
});
/* eslint-enable @atlaskit/ui-styling-standard/no-nested-selectors */

function clampLoadEntranceSpeedMultiplier(raw: number | undefined): number {
    const m = raw ?? 1;
    if (!Number.isFinite(m) || m <= 0) {
        return 1;
    }
    return Math.min(10, Math.max(0.25, m));
}

function computePhaseClockStartOffsetSec(
    animateOnLoad: boolean,
    nodeCount: number,
    edgeCount: number,
    nodeTotalDuration: number,
    connectorType: ConnectorType,
    nodeStagger: number,
    nodeEntranceDurationSec: number,
    edgeDelayBase: number,
    edgeStagger: number,
    solidDrawDurationSec: number,
    dashedFadeDurationSec: number
): number {
    if (!animateOnLoad) {
        return 0;
    }
    const lastNodeDelay = nodeCount > 0 ? (nodeCount - 1) * nodeStagger : 0;
    const nodeEntranceEndSec = lastNodeDelay + nodeEntranceDurationSec;

    const connectorEntranceEndSec =
        edgeCount === 0
            ? 0
            : nodeTotalDuration +
              edgeDelayBase +
              Math.max(0, edgeCount - 1) * edgeStagger +
              (connectorType === "dashed"
                  ? dashedFadeDurationSec
                  : solidDrawDurationSec);

    return Math.max(nodeEntranceEndSec, connectorEntranceEndSec);
}

function createNodeEntranceDelayFn(
    animateOnLoad: boolean,
    nodeStagger: number
) {
    return (index: number) => (animateOnLoad ? index * nodeStagger : 0);
}

function createConnectorEntranceDelayFn(
    animateOnLoad: boolean,
    nodeTotalDuration: number,
    edgeDelayBase: number,
    edgeStagger: number
) {
    return (index: number) =>
        animateOnLoad
            ? nodeTotalDuration + edgeDelayBase + index * edgeStagger
            : 0;
}

function overlapEntranceDelayValue(
    animateOnLoad: boolean,
    nodeTotalDuration: number,
    edgeDelayBase: number
): number {
    return animateOnLoad ? nodeTotalDuration + edgeDelayBase * 0.5 : 0;
}

function openNodeModalIfPresent(
    node: WebNodeConfig,
    setExpandingNodeId: (id: string | null) => void,
    setActiveModal: (content: ModalContent | null) => void
): void {
    if (!node.modalContent) {
        return;
    }
    setExpandingNodeId(node.id);
    setTimeout(() => {
        setActiveModal(node.modalContent ?? null);
    }, 200);
}

function resolveAnimationFlags(
    prefersReducedMotion: boolean,
    isVisible: boolean
): { animated: boolean; phaseMotionActive: boolean } {
    return {
        animated: prefersReducedMotion || isVisible,
        phaseMotionActive: !prefersReducedMotion && isVisible
    };
}

function spiderWebDimensionValues(
    width: number | string | undefined,
    height: number | string | undefined
): { widthValue: number | string; heightValue: number | string } {
    return {
        widthValue: width ?? "100%",
        heightValue: height ?? 600
    };
}

function computeViewBoxHeight(
    width: number | string,
    height: number | string,
    useViewportDimensions?: boolean
): number {
    if (useViewportDimensions) {
        return VIEWBOX_SCALE;
    }
    if (typeof width === "number" && typeof height === "number") {
        return Math.round((VIEWBOX_SCALE * height) / width);
    }
    return VIEWBOX_SCALE;
}

function buildContainerStyle(
    width?: number | string,
    height?: number | string,
    bgColor?: FillColor,
    maxHeightViewport?: boolean,
    useViewportDimensions?: boolean
): React.CSSProperties {
    const bg = bgColor !== undefined ? getFillColor(bgColor) : undefined;

    if (useViewportDimensions) {
        return {
            position: "relative" as const,
            backgroundColor: bg,
            overflow: "hidden" as const,
            width: "90vw",
            height: "90vh"
        };
    }

    const hasExplicitWidth =
        (width && typeof width === "number") || typeof width === "string";
    const hasExplicitHeight =
        (height && typeof height === "number") || typeof height === "string";

    return {
        position: "relative" as const,
        backgroundColor: bg,
        overflow: "hidden" as const,
        maxWidth: width,
        width: "100%",
        maxHeight: maxHeightViewport
            ? "90vh"
            : !hasExplicitWidth && !hasExplicitHeight
            ? height
            : undefined,
        height: hasExplicitHeight && !hasExplicitWidth ? height : undefined,
        aspectRatio:
            hasExplicitWidth && hasExplicitHeight
                ? `${width} / ${height}`
                : undefined
    };
}

/* eslint-disable complexity -- orchestrates graph, phases, connectors, modal, and optional pause UI */
export const SpiderWebContainer = ({
    nodes,
    connectors: explicitConnectors,
    useOnlyExplicitConnectors = false,
    connectorShape = "straight",
    connectorType = "solid",
    bgColor,
    width,
    height,
    maxNeighbors = 3,
    connectorColor = "neutral700",
    connectorWidth = 2,
    fontFamily = "monospace",
    animateOnLoad = true,
    loadEntranceSpeedMultiplier,
    replayOnVisible = false,
    maxHeightViewport = false,
    useViewportDimensions = false,
    showAnimationPauseControl = false,
    animationPhasesDrive = "time",
    scrollSectionRef,
    onSvgCanvasClick,
    onNodeClick,
    focusedConnector,
    nodeFocus
}: SpiderWebContainerProps) => {
    const containerRef = useRef<HTMLDivElement>(null);
    const isVisible = useSpiderWebIntersectionVisibility(
        containerRef,
        replayOnVisible
    );
    const prefersReducedMotion = useSpiderWebReducedMotion();
    const [activeModal, setActiveModal] = useState<ModalContent | null>(null);
    const [expandingNodeId, setExpandingNodeId] = useState<string | null>(null);
    const [animationsPaused, setAnimationsPaused] = useState(false);

    const { animated, phaseMotionActive } = resolveAnimationFlags(
        prefersReducedMotion,
        isVisible
    );

    const entranceM = clampLoadEntranceSpeedMultiplier(
        loadEntranceSpeedMultiplier
    );

    const loadEntranceTimings = useMemo(
        () => ({
            nodeStagger: NODE_STAGGER / entranceM,
            edgeDelayBase: EDGE_DELAY_BASE / entranceM,
            edgeStagger: EDGE_STAGGER / entranceM,
            nodeEntranceDurationSec: NODE_ENTRANCE_DURATION_SEC / entranceM,
            connectorSolidDrawDurationSec:
                CONNECTOR_SOLID_DRAW_DURATION_SEC / entranceM,
            connectorDashedFadeDurationSec:
                CONNECTOR_DASHED_ENTRANCE_DURATION_SEC / entranceM,
            overlapFadeDurationSec: OVERLAP_LOAD_ENTRANCE_FADE_SEC / entranceM,
            animatedShapeOpacityDurationSec:
                ANIMATED_CONNECTOR_SHAPE_OPACITY_SEC / entranceM,
            animatedShapeAppearOffsetSec:
                ANIMATED_CONNECTOR_SHAPE_APPEAR_OFFSET_SEC / entranceM
        }),
        [entranceM]
    );

    const nodeTotalDuration = nodes.length * loadEntranceTimings.nodeStagger;

    const { connectorConfigs, resolvedConnectors } = useSpiderWebConnectorPlan(
        nodes,
        maxNeighbors,
        explicitConnectors,
        useOnlyExplicitConnectors,
        connectorShape,
        connectorType
    );

    const phaseClockStartOffsetSec = useMemo(
        () =>
            computePhaseClockStartOffsetSec(
                animateOnLoad,
                nodes.length,
                connectorConfigs.length,
                nodeTotalDuration,
                connectorType,
                loadEntranceTimings.nodeStagger,
                loadEntranceTimings.nodeEntranceDurationSec,
                loadEntranceTimings.edgeDelayBase,
                loadEntranceTimings.edgeStagger,
                loadEntranceTimings.connectorSolidDrawDurationSec,
                loadEntranceTimings.connectorDashedFadeDurationSec
            ),
        [
            animateOnLoad,
            nodes.length,
            connectorConfigs.length,
            nodeTotalDuration,
            connectorType,
            loadEntranceTimings.connectorDashedFadeDurationSec,
            loadEntranceTimings.connectorSolidDrawDurationSec,
            loadEntranceTimings.edgeDelayBase,
            loadEntranceTimings.edgeStagger,
            loadEntranceTimings.nodeEntranceDurationSec,
            loadEntranceTimings.nodeStagger
        ]
    );

    const skipLoadEntrance = !animateOnLoad;
    const nodeEntranceDelay = useMemo(
        () =>
            createNodeEntranceDelayFn(
                animateOnLoad,
                loadEntranceTimings.nodeStagger
            ),
        [animateOnLoad, loadEntranceTimings.nodeStagger]
    );
    const connectorEntranceDelay = useMemo(
        () =>
            createConnectorEntranceDelayFn(
                animateOnLoad,
                nodeTotalDuration,
                loadEntranceTimings.edgeDelayBase,
                loadEntranceTimings.edgeStagger
            ),
        [
            animateOnLoad,
            loadEntranceTimings.edgeDelayBase,
            loadEntranceTimings.edgeStagger,
            nodeTotalDuration
        ]
    );
    const overlapEntranceDelay = overlapEntranceDelayValue(
        animateOnLoad,
        nodeTotalDuration,
        loadEntranceTimings.edgeDelayBase
    );

    const hasAnyPhases = useMemo(() => anyNodeHasPhases(nodes), [nodes]);
    const scrollPhasesEnabled =
        animationPhasesDrive === "scroll" && hasAnyPhases;
    const {
        progress: scrollPhaseProgress,
        isAttached: scrollProgressAttached
    } = useSpiderwebDocumentSectionProgress(
        scrollSectionRef,
        scrollPhasesEnabled
    );

    const phaseDriveActive =
        animationPhasesDrive === "scroll"
            ? scrollProgressAttached
            : phaseMotionActive;
    const effectivePhaseClockOffsetSec =
        animationPhasesDrive === "scroll" ? 0 : phaseClockStartOffsetSec;

    const nodePositions = useSpiderwebPhasePositions(
        nodes,
        phaseDriveActive,
        effectivePhaseClockOffsetSec,
        animationsPaused,
        {
            drive: animationPhasesDrive,
            scrollProgress: scrollPhaseProgress
        }
    );

    const aspectCorrection = useSpiderwebViewportAspect(useViewportDimensions);

    const handleNodeActivate = useCallback(
        (node: WebNodeConfig) => {
            onNodeClick?.(node);
            openNodeModalIfPresent(node, setExpandingNodeId, setActiveModal);
        },
        [onNodeClick]
    );

    useEffect(() => {
        const root = containerRef.current;
        if (!root) {
            return;
        }
        const svg = root.querySelector("svg");
        if (!(svg instanceof SVGSVGElement)) {
            return;
        }
        if (animationsPaused) {
            svg.pauseAnimations?.();
        } else {
            svg.unpauseAnimations?.();
        }
    }, [animationsPaused]);

    const { widthValue, heightValue } = spiderWebDimensionValues(width, height);
    const viewBoxHeight = computeViewBoxHeight(
        widthValue,
        heightValue,
        useViewportDimensions
    );

    return (
        <div
            ref={containerRef}
            css={spiderwebPauseScopeStyle}
            data-spiderweb-animations-paused={
                animationsPaused ? "true" : undefined
            }
            style={buildContainerStyle(
                width,
                height,
                bgColor,
                maxHeightViewport,
                useViewportDimensions
            )}
        >
            <SpiderWebContainerGraph
                viewBoxHeight={viewBoxHeight}
                useViewportDimensions={useViewportDimensions}
                aspectCorrection={aspectCorrection}
                resolvedConnectors={resolvedConnectors}
                nodes={nodes}
                animated={animated}
                skipLoadEntrance={skipLoadEntrance}
                connectorColor={connectorColor}
                connectorWidth={connectorWidth}
                connectorEntranceDelay={connectorEntranceDelay}
                nodeEntranceDelay={nodeEntranceDelay}
                overlapEntranceDelay={overlapEntranceDelay}
                nodeLoadEntranceDurationSec={
                    loadEntranceTimings.nodeEntranceDurationSec
                }
                connectorLoadEntranceSolidDrawDurationSec={
                    loadEntranceTimings.connectorSolidDrawDurationSec
                }
                connectorLoadEntranceDashedFadeDurationSec={
                    loadEntranceTimings.connectorDashedFadeDurationSec
                }
                connectorLoadEntranceAnimatedShapeOpacityDurationSec={
                    loadEntranceTimings.animatedShapeOpacityDurationSec
                }
                connectorLoadEntranceAnimatedShapeAppearOffsetSec={
                    loadEntranceTimings.animatedShapeAppearOffsetSec
                }
                overlapLoadEntranceFadeDurationSec={
                    loadEntranceTimings.overlapFadeDurationSec
                }
                fontFamily={fontFamily}
                expandingNodeId={expandingNodeId}
                onNodeActivate={handleNodeActivate}
                allowNodeActivationWithoutModal={onNodeClick !== undefined}
                nodePositions={nodePositions}
                onSvgCanvasClick={onSvgCanvasClick}
                focusedConnector={focusedConnector}
                nodeFocus={nodeFocus}
            />

            {showAnimationPauseControl ? (
                <button
                    type="button"
                    style={{
                        position: "absolute",
                        top: 0,
                        right: 0,
                        zIndex: 1
                    }}
                    onClick={() => setAnimationsPaused((p) => !p)}
                >
                    {animationsPaused
                        ? "Resume animations"
                        : "Pause animations"}
                </button>
            ) : null}

            {activeModal && (
                <AnimatedModal
                    content={activeModal}
                    onClose={() => {
                        setActiveModal(null);
                        setExpandingNodeId(null);
                    }}
                />
            )}
        </div>
    );
};
/* eslint-enable complexity */
